/**
 * Public tenant-facing intake for the Apartment/House Lease flow.
 * Route:  /lease-intake  (permanent, single link the admin sends).
 *
 * Mirrors /paystub-intake in look & structure but drives the 4-step
 * lease shape: Landlord → Property → Tenant → Rent & Terms → Sign.
 * Every step blocks Next until required fields are filled — you asked
 * "won't let them continue" and this enforces exactly that.
 *
 * On submit we POST to /api/lease-intake/submit which
 *   1. stores the row in Mongo,
 *   2. emails clearallcreditrepair@gmail.com,
 *   3. returns any AI/heuristic sanity warnings so the tenant sees
 *      them BEFORE they lock it in.
 */
import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import {
  CheckCircle2, Send, Home, User, DollarSign, PenLine, ChevronRight,
  ChevronLeft, AlertTriangle, Upload, Trash2, FileText, Shield,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const initial = {
  // Landlord (tenant may not know)
  company_name: "", landlord_name: "", landlord_unknown: false,
  landlord_phone: "", landlord_email: "",
  // Property
  property_address: "", property_unit: "",
  property_city: "", property_state: "", property_zip: "",
  // Tenant
  tenant_names: "", tenant_phone: "", tenant_email: "",
  tenant_address: "", tenant_city: "", tenant_state: "", tenant_zip: "",
  // Rent & Terms
  term_type: "fixed", start_date: "", end_date: "", move_in_date: "",
  monthly_rent: "", security_deposit: "",
  late_fee_amount: "50", grace_days: "5", nsf_fee: "35",
  // Utilities & house rules (optional)
  include_utilities_section: true,
  utilities_included: "", pets_allowed: false, pet_deposit: "",
  smoking_allowed: false, max_occupants: "",
  include_additional_terms: false, additional_terms: "",
  // Signature + initials images (data-URLs).  Captured on Step 5.
  tenant_signature_image: "", tenant_initials_image: "",
};

// Field-level required lists per step.  Blocks Next until clear.
const stepRequired = {
  1: (f) => {
    if (f.landlord_unknown) return [];
    const missing = [];
    if (!f.company_name.trim()) missing.push("Company / Property Name");
    if (!f.landlord_name.trim()) missing.push("Landlord Name");
    return missing;
  },
  2: (f) => {
    const missing = [];
    if (!f.property_address.trim()) missing.push("Property Address");
    if (!f.property_city.trim()) missing.push("City");
    if (!f.property_state.trim()) missing.push("State");
    if (!f.property_zip.trim()) missing.push("ZIP");
    return missing;
  },
  3: (f) => {
    const missing = [];
    if (!f.tenant_names.trim()) missing.push("Tenant Full Name");
    if (!f.tenant_phone.trim()) missing.push("Tenant Phone");
    if (!f.tenant_email.trim()) missing.push("Tenant Email");
    return missing;
  },
  4: (f) => {
    const missing = [];
    if (!f.start_date) missing.push("Lease Start Date");
    if (f.term_type === "fixed" && !f.end_date) missing.push("Lease End Date");
    if (!f.move_in_date) missing.push("Move-in Date");
    if (!f.monthly_rent) missing.push("Monthly Rent");
    if (!f.security_deposit) missing.push("Security Deposit");
    return missing;
  },
  5: (f) => {
    const missing = [];
    if (!f.tenant_signature_image) missing.push("Full Signature");
    if (!f.tenant_initials_image) missing.push("Initials");
    return missing;
  },
};

const stepTitles = [
  { title: "Landlord Info", icon: Home,      desc: "Who owns / manages the property?" },
  { title: "Property",      icon: Home,      desc: "Address of the place you're leasing" },
  { title: "Your Info",     icon: User,      desc: "The person(s) on the lease" },
  { title: "Rent & Terms",  icon: DollarSign,desc: "Money side + start / end dates" },
  { title: "Sign",          icon: PenLine,   desc: "Full signature + initials" },
];

export default function LeaseIntakePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);        // { submission_id, warnings }
  const [warnAccepted, setWarnAccepted] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const missing = useMemo(() => stepRequired[step]?.(form) || [], [step, form]);
  const canAdvance = missing.length === 0;

  const goNext = () => {
    if (!canAdvance) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    if (step < 5) setStep(step + 1);
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const doSubmit = async () => {
    if (!canAdvance) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/lease-intake/submit`, form);
      setResult(res.data);
      if ((res.data.warnings || []).length === 0) {
        // No warnings — flow straight to Thank You.
        toast.success("Submitted!");
      }
    } catch (e) {
      toast.error("Submit failed. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Thank-You screen (also handles the "warnings — please review"
  // interstitial when the sanity pass found something).
  // ------------------------------------------------------------------
  if (result) {
    const warns = result.warnings || [];
    if (warns.length > 0 && !warnAccepted) {
      return (
        <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-8">
          <Toaster theme="dark" position="top-center" />
          <div className="max-w-xl mx-auto bg-gradient-to-br from-amber-900/40 to-slate-900 border border-amber-500/40 rounded-2xl p-6 mt-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-300" />
              <h1 className="text-xl font-bold">We noticed a few things — please review</h1>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              Your info is saved.  Take one more look at these items — if
              they're right, tap <b>Send it anyway</b> below.  If not, tap
              <b> Edit my answers</b> to fix them.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-amber-200 mb-6">
              {warns.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <div className="flex gap-2">
              <button
                onClick={() => { setResult(null); }}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold py-2.5 rounded-xl"
                data-testid="intake-warn-edit-btn"
              >
                Edit my answers
              </button>
              <button
                onClick={() => setWarnAccepted(true)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-2.5 rounded-xl"
                data-testid="intake-warn-continue-btn"
              >
                Send it anyway
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
        <Toaster theme="dark" position="top-center" />
        <div className="max-w-lg w-full bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/40 rounded-2xl p-8 text-center shadow-2xl">
          <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Thanks — we've got everything!</h1>
          <p className="text-sm text-slate-300">
            Your lease intake was submitted successfully.  Your leasing agent
            will follow up with the finished document by email.  You can
            close this tab now.
          </p>
          <p className="text-xs text-slate-500 mt-6">
            Reference #: <span className="font-mono">{result.submission_id?.slice(0, 8)}</span>
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Main form
  // ------------------------------------------------------------------
  const StepIcon = stepTitles[step - 1].icon;
  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-8">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <FileText className="w-10 h-10 text-fuchsia-400 mx-auto mb-2" />
          <h1 className="text-2xl sm:text-3xl font-bold">Lease Intake</h1>
          <p className="text-sm text-slate-400 mt-1">
            Fill every step — takes about 5 minutes.
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-6">
          {stepTitles.map((s, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i + 1 < step ? "w-6 bg-emerald-500"
                : i + 1 === step ? "w-10 bg-fuchsia-500"
                : "w-6 bg-white/10"
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-slate-900/70 border border-white/10 rounded-2xl p-5 sm:p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
              <StepIcon className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
                Step {step} of 5
              </div>
              <div className="text-lg font-bold">{stepTitles[step - 1].title}</div>
              <div className="text-xs text-slate-400">{stepTitles[step - 1].desc}</div>
            </div>
          </div>

          {step === 1 && <Step1Landlord form={form} set={set} setVal={setVal} />}
          {step === 2 && <Step2Property form={form} set={set} setVal={setVal} />}
          {step === 3 && <Step3Tenant   form={form} set={set} setVal={setVal} />}
          {step === 4 && <Step4Rent     form={form} set={set} setVal={setVal} />}
          {step === 5 && <Step5Sign     form={form} setVal={setVal} />}

          {/* Nav */}
          <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
            <button
              onClick={goBack}
              disabled={step === 1}
              className="inline-flex items-center gap-1 bg-white/[0.04] hover:bg-white/[0.10] disabled:opacity-40 text-slate-200 text-sm font-medium py-2 px-3 rounded-lg"
              data-testid="intake-back-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-[11px] text-slate-500">
              {missing.length > 0 && (
                <span>Missing: <span className="text-amber-300">{missing.join(" · ")}</span></span>
              )}
            </div>
            {step < 5 ? (
              <button
                onClick={goNext}
                disabled={!canAdvance}
                className="inline-flex items-center gap-1 bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-40 text-white text-sm font-semibold py-2 px-4 rounded-lg"
                data-testid="intake-next-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={doSubmit}
                disabled={!canAdvance || submitting}
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold py-2 px-4 rounded-lg"
                data-testid="intake-submit-btn"
              >
                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Submit"}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-5">
          <Shield className="w-3 h-3 inline-block mr-1 align-text-bottom" />
          Encrypted in transit · your info goes only to your leasing agent.
        </p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Step blocks — small stateless views.  Any input change bubbles up via
// the `set` / `setVal` helpers passed from the parent.
// ─────────────────────────────────────────────────────────────────────

const inputCls = "w-full bg-slate-950 border border-white/10 focus:border-fuchsia-400/60 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none";
const labelCls = "block text-xs font-semibold text-slate-300 mb-1";

function Field({ label, ...props }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input className={inputCls} {...props} />
    </div>
  );
}


function Step1Landlord({ form, set, setVal }) {
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 bg-slate-950 border border-white/10 rounded-lg px-3 py-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.landlord_unknown}
          onChange={set("landlord_unknown")}
          data-testid="intake-landlord-unknown"
        />
        <span className="text-sm">
          I <b>don't know</b> the landlord/company yet — your agent can fill this in.
        </span>
      </label>
      {!form.landlord_unknown && (
        <>
          <Field label="Company / Property Name *" value={form.company_name} onChange={set("company_name")} data-testid="intake-company-name" />
          <Field label="Landlord Name *" value={form.landlord_name} onChange={set("landlord_name")} data-testid="intake-landlord-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Landlord Phone" value={form.landlord_phone} onChange={set("landlord_phone")} placeholder="(555) 123-4567" data-testid="intake-landlord-phone" />
            <Field label="Landlord Email" value={form.landlord_email} onChange={set("landlord_email")} placeholder="agent@example.com" data-testid="intake-landlord-email" />
          </div>
        </>
      )}
    </div>
  );
}

function Step2Property({ form, set }) {
  return (
    <div className="space-y-3">
      <Field label="Property Address *" value={form.property_address} onChange={set("property_address")} placeholder="123 Main St" data-testid="intake-property-address" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Apt / Unit #" value={form.property_unit} onChange={set("property_unit")} data-testid="intake-property-unit" />
        <Field label="City *" value={form.property_city} onChange={set("property_city")} data-testid="intake-property-city" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="State *" value={form.property_state} onChange={set("property_state")} placeholder="CA" maxLength={2} data-testid="intake-property-state" />
        <Field label="ZIP *" value={form.property_zip} onChange={set("property_zip")} placeholder="90001" data-testid="intake-property-zip" />
      </div>
    </div>
  );
}

function Step3Tenant({ form, set }) {
  return (
    <div className="space-y-3">
      <Field label="Full Legal Name(s) on the Lease *" value={form.tenant_names} onChange={set("tenant_names")} placeholder="John Doe & Jane Doe" data-testid="intake-tenant-names" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone *" value={form.tenant_phone} onChange={set("tenant_phone")} placeholder="(555) 123-4567" data-testid="intake-tenant-phone" />
        <Field label="Email *" value={form.tenant_email} onChange={set("tenant_email")} placeholder="you@example.com" data-testid="intake-tenant-email" />
      </div>
      <div className="text-[11px] text-slate-500 mt-4">Current mailing address (optional — for ID only)</div>
      <Field label="Current Address" value={form.tenant_address} onChange={set("tenant_address")} data-testid="intake-tenant-address" />
      <div className="grid grid-cols-3 gap-3">
        <Field label="City" value={form.tenant_city} onChange={set("tenant_city")} data-testid="intake-tenant-city" />
        <Field label="State" value={form.tenant_state} onChange={set("tenant_state")} maxLength={2} data-testid="intake-tenant-state" />
        <Field label="ZIP" value={form.tenant_zip} onChange={set("tenant_zip")} data-testid="intake-tenant-zip" />
      </div>
    </div>
  );
}

function Step4Rent({ form, set }) {
  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>Lease Term Type *</label>
        <div className="flex gap-2">
          {["fixed", "month_to_month"].map((t) => (
            <label key={t} className={`flex-1 flex items-center gap-2 justify-center py-2 px-3 rounded-lg border cursor-pointer text-sm ${
              form.term_type === t ? "bg-fuchsia-600/25 border-fuchsia-400" : "bg-slate-950 border-white/10"
            }`}>
              <input type="radio" className="accent-fuchsia-500" name="term_type" checked={form.term_type === t} onChange={() => set("term_type")({ target: { value: t } })} />
              {t === "fixed" ? "Fixed Term" : "Month-to-Month"}
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lease Start Date *" type="date" value={form.start_date} onChange={set("start_date")} data-testid="intake-start-date" />
        {form.term_type === "fixed" && (
          <Field label="Lease End Date *" type="date" value={form.end_date} onChange={set("end_date")} data-testid="intake-end-date" />
        )}
      </div>
      <Field label="Move-in Date *" type="date" value={form.move_in_date} onChange={set("move_in_date")} data-testid="intake-move-in-date" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Monthly Rent ($) *" type="number" min="0" step="1" value={form.monthly_rent} onChange={set("monthly_rent")} placeholder="1500" data-testid="intake-monthly-rent" />
        <Field label="Security Deposit ($) *" type="number" min="0" step="1" value={form.security_deposit} onChange={set("security_deposit")} placeholder="1500" data-testid="intake-security-deposit" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Late Fee ($)" type="number" min="0" value={form.late_fee_amount} onChange={set("late_fee_amount")} data-testid="intake-late-fee" />
        <Field label="Grace Days" type="number" min="0" max="30" value={form.grace_days} onChange={set("grace_days")} data-testid="intake-grace-days" />
        <Field label="NSF Fee ($)" type="number" min="0" value={form.nsf_fee} onChange={set("nsf_fee")} data-testid="intake-nsf-fee" />
      </div>
      <details className="mt-4 bg-slate-950 border border-white/10 rounded-lg p-3">
        <summary className="text-xs font-semibold text-slate-300 cursor-pointer">Utilities & house rules (optional)</summary>
        <div className="mt-3 space-y-3">
          <Field label="Utilities Included in Rent" value={form.utilities_included} onChange={set("utilities_included")} placeholder="Water, trash" />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pets_allowed} onChange={set("pets_allowed")} /> Pets allowed</label>
            {form.pets_allowed && (
              <Field label="Pet Deposit ($)" type="number" value={form.pet_deposit} onChange={set("pet_deposit")} />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smoking_allowed} onChange={set("smoking_allowed")} /> Smoking allowed</label>
            <Field label="Max Occupants" type="number" value={form.max_occupants} onChange={set("max_occupants")} />
          </div>
        </div>
      </details>
    </div>
  );
}


// -- Step 5: signature capture (DocuSign-style, trackpad-friendly) ----

function SignatureBlock({ label, testidBase, value, setValue, canvasHeight = 140 }) {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState(value ? "saved" : "draw");   // draw | upload | saved

  const clearCanvas = () => {
    canvasRef.current?.clear();
    setValue("");
    setMode("draw");
  };

  const commitDraw = () => {
    if (canvasRef.current?.isEmpty()) {
      toast.error(`Please draw your ${label.toLowerCase()} first`);
      return;
    }
    const dataUrl = canvasRef.current.getCanvas().toDataURL("image/png");
    setValue(dataUrl);
    setMode("saved");
    toast.success(`${label} captured ✓`);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large — please pick something under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setValue(reader.result);
      setMode("saved");
      toast.success(`${label} uploaded ✓`);
    };
    reader.readAsDataURL(file);
  };

  if (mode === "saved" && value) {
    return (
      <div className="bg-slate-950 border border-emerald-500/30 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-emerald-300">✓ {label} captured</span>
          <button
            onClick={() => { setValue(""); setMode("draw"); }}
            className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1"
            data-testid={`${testidBase}-redo-btn`}
          >
            <Trash2 className="w-3 h-3" /> Redo
          </button>
        </div>
        <img src={value} alt={label} className="max-h-24 bg-white rounded" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-1.5 mb-2">
        {[
          ["draw", "Draw with trackpad"],
          ["upload", "Upload image"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            onClick={() => setMode(k)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              mode === k ? "bg-fuchsia-600 border-fuchsia-400 text-white" : "bg-slate-950 border-white/10 text-slate-400"
            }`}
            data-testid={`${testidBase}-mode-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {mode === "draw" && (
        <div>
          <div className="bg-white rounded-lg overflow-hidden">
            <SignatureCanvas
              ref={canvasRef}
              penColor="black"
              canvasProps={{
                className: "w-full",
                style: { touchAction: "none", height: canvasHeight },
                "data-testid": `${testidBase}-canvas`,
              }}
            />
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={clearCanvas}
              className="flex-1 text-[12px] py-1.5 bg-white/10 hover:bg-white/20 rounded"
              data-testid={`${testidBase}-clear-btn`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={commitDraw}
              className="flex-1 text-[12px] py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded font-semibold"
              data-testid={`${testidBase}-save-btn`}
            >
              Save {label}
            </button>
          </div>
        </div>
      )}

      {mode === "upload" && (
        <div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 bg-slate-950 border border-dashed border-white/20 hover:border-fuchsia-400/50 rounded-lg py-6 text-sm text-slate-400"
            data-testid={`${testidBase}-upload-btn`}
          >
            <Upload className="w-4 h-4" /> Pick image from device
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </div>
      )}
    </div>
  );
}


function Step5Sign({ form, setVal }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 leading-relaxed">
        We need <b>two things</b> from you:
        your <b>full signature</b> (goes on the signature block at the end
        of the lease) and your <b>initials</b> (appear at the bottom of
        every page).  Draw with your trackpad/mouse, or upload an image
        from your device.
      </p>

      <div>
        <label className={labelCls}>Your Full Signature *</label>
        <SignatureBlock
          label="Signature"
          testidBase="intake-sig-full"
          value={form.tenant_signature_image}
          setValue={(v) => setVal("tenant_signature_image", v)}
          canvasHeight={160}
        />
      </div>

      <div>
        <label className={labelCls}>Your Initials *</label>
        <SignatureBlock
          label="Initials"
          testidBase="intake-sig-initials"
          value={form.tenant_initials_image}
          setValue={(v) => setVal("tenant_initials_image", v)}
          canvasHeight={110}
        />
      </div>

      <div className="text-[11px] text-slate-500 bg-slate-950 border border-white/10 rounded-lg p-3 mt-4">
        By submitting this form, you agree that the drawings above will
        be used as your <b>electronic signature</b> on the lease your
        agent generates.  You'll receive a copy of the final document
        by email.
      </div>
    </div>
  );
}
